import React, { useMemo, type FC, type ReactNode } from 'react'
import {
  type TableColumnType,
  type TableProps,
  ConfigProvider,
  Flex,
  Table,
} from 'antd'
import { useTableSorting } from '@choreograph/layout'

import { type IOktaUser } from '../../../constants/types/generatedTypesAliases'
import { getTablePaginationProp, userStaticColumns } from '../../../constants/helpers/common'
import { EmptyTablePlaceholder } from '../../shared'
import { UserTableFilters } from './UserTableFilters'

type TableRowSelection<T extends object = object> = TableProps<T>['rowSelection']

interface IUserTableProps extends TableProps<IOktaUser> {
  tableData: IOktaUser[]
  loading: boolean
  rowActionButtons?: TableColumnType<IOktaUser>
  bulkActionButtons?: ReactNode
  selectedRowKeys: string[]
  setSelectedRowKeys: (keys: string[]) => void
  searchFilter: any
  setSearchFilter: (keys: any) => void
}

export const UserTable: FC<IUserTableProps> = ({
  tableData,
  loading,
  selectedRowKeys,
  setSelectedRowKeys,
  rowActionButtons,
  bulkActionButtons,
  searchFilter,
  setSearchFilter,
  ...rest
}) => {
  const { handleSortable } = useTableSorting(true)

  const pagination = useMemo(() => (
    getTablePaginationProp<IOktaUser>('users', tableData.length)
  ), [tableData.length])

  const columns: Array<TableColumnType<IOktaUser>> = useMemo(() => (
    [
      ...userStaticColumns,
      ...(rowActionButtons) ? [rowActionButtons] : []
    ].map(c => handleSortable(c))
  ), [handleSortable, rowActionButtons])

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys as string[])
  }

  const rowSelection: TableRowSelection<IOktaUser> = {
    selectedRowKeys,
    onChange: onSelectChange,
    preserveSelectedRowKeys: true
  }

  return (
    <ConfigProvider
      renderEmpty={() => (
        <EmptyTablePlaceholder
          loading={loading}
          title='No Users'
          message=' There are currently no users to view' />
      )}>
      <Flex justify='space-between' align='center'>
        {/* Fillters */}
        <UserTableFilters setSearchFilter={setSearchFilter} searchFilter={searchFilter} />
        {/* Actions */}
        <Flex gap={12}>
          {
            bulkActionButtons && selectedRowKeys.length
              ? (bulkActionButtons)
              : null
          }
        </Flex>
      </Flex>
      <Table
        loading={loading}
        rowKey='id'
        columns={columns}
        dataSource={tableData}
        rowSelection={rowSelection}
        {...pagination}
        {...rest}
      />
    </ConfigProvider>
  )
}
